import React, { useRef } from 'react';

/**
 * CRM Invoice Generator
 * 100% inline styles for pixel-perfect printing.
 */
export default function CRMInvoice({ order, customer, products, generalSettings, shippingFees, onClose, settings }) {
    const printRef = useRef(null);
    if (!order) return null;

    const PRIMARY_COLOR_MAP = {
        'red': { 600: '#dc2626', 50: '#fef2f2' }, 'orange': { 600: '#ea580c', 50: '#fff7ed' },
        'amber': { 600: '#d97706', 50: '#fffbeb' }, 'yellow': { 600: '#ca8a04', 50: '#fefce8' },
        'lime': { 600: '#65a30d', 50: '#f7fee7' }, 'green': { 600: '#16a34a', 50: '#f0fdf4' },
        'teal': { 600: '#0d9488', 50: '#f0fdfa' }, 'cyan': { 600: '#0891b2', 50: '#ecfeff' },
        'blue': { 600: '#2563eb', 50: '#eff6ff' }, 'indigo': { 600: '#4f46e5', 50: '#f5f3ff' },
        'violet': { 600: '#35006B', 50: '#F6F0FF' }, 'purple': { 600: '#7c3aed', 50: '#f5f3ff' },
        'fuchsia': { 600: '#c026d3', 50: '#fdf4ff' }, 'pink': { 600: '#db2777', 50: '#fdf2f8' },
        'rose': { 600: '#e11d48', 50: '#fff1f2' },
        'black': { 600: '#000000', 50: '#f9fafb' }
    };

    const themeColor = settings?.primaryColor || 'indigo';
    const shades = PRIMARY_COLOR_MAP[themeColor] || PRIMARY_COLOR_MAP['indigo'];

    const inv = generalSettings?.invoiceSettings || {};
    const c           = shades[600];
    const bgLight     = shades[50];
    const logoUrl     = inv.logoUrl || generalSettings?.logoUrl || '';
    const showEmail   = inv.showEmail !== false;
    const footerText  = inv.footerText || `Thank you for your purchase from ${generalSettings?.storeName || 'Lilac Valley'}!`;
    const companyName = generalSettings?.storeName || 'Lilac Valley';
    const currency    = generalSettings?.currency || 'EGP';

    const prodMap = new Map((products || []).map(p => [p.product, p]));

    const lineItems = (order.items || []).map(item => {
        const prod = prodMap.get(item.pid);
        const unitPrice = prod ? parseFloat(prod.price) || 0 : 0;
        const qty = parseInt(item.qty) || 1;
        const imageUrl = prod?.imageurl || '';
        return { name: item.pid, qty, unitPrice, total: unitPrice * qty, imageUrl };
    });

    const shippingFee = (() => {
        if (!shippingFees || !customer) return 0;
        const fee = shippingFees.find(f =>
            f.city?.toLowerCase() === (customer.city || '').toLowerCase() &&
            f.state?.toLowerCase() === (customer.state || '').toLowerCase()
        ) || shippingFees.find(f =>
            f.state?.toLowerCase() === (customer.state || '').toLowerCase()
        );
        return fee ? parseFloat(fee.fees) || 0 : 0;
    })();

    const subtotal  = lineItems.reduce((sum, i) => sum + i.total, 0);
    const discount  = parseFloat(order.discount) || 0;
    const total     = parseFloat(order.total) || (subtotal + shippingFee - discount);
    const orderDate = order.date ? new Date(order.date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB');

    const invoiceNo  = order.orderid || `#${order.id || ''}`;

    const wooOrderId = order.woo_order_id || order.order_id
        || order.wooOrderData?.id || order.wooOrderData?.number || order.wooOrderData?.order_id
        || order.wooOrderData?.customer_data?.woo_order_id
        || order.notes?.match(/\[(?:WooCommerce|Lilacvalley) Order ID: #?(\d+)\]/i)?.[1]
        || (order.orderid && !order.orderid.startsWith('#o') ? order.orderid : '')
        || '';

    const addressLine = [
        customer?.address_1 || customer?.address,
        customer?.address_2, customer?.city, customer?.state, customer?.postcode
    ].filter(Boolean).join(', ');

    // Is it paid? (Visa, card, mobile wallet = paid)
    const paymentLower = (order.payment || '').toLowerCase();
    const isPaid = ['visa', 'card', 'mobile wallet', 'mobile_wallet', 'credit', 'debit', 'paymob', 'fawry', 'mastercard', 'online'].some(k => paymentLower.includes(k));

    let customerNotes = order.notes || order.wooOrderData?.customer_data?.customer_note || '';
    // Strip WooCommerce Order ID from customer notes to avoid duplication
    customerNotes = customerNotes.replace(/\[(?:WooCommerce|Lilacvalley) Order ID: #?\d+\]/gi, '').trim();

    const handlePrint = () => {
        const content = printRef.current.innerHTML;
        const w = window.open('', '_blank');
        w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice ${invoiceNo}</title>
<style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#1a1a1a;padding:24px; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;}
    @media print {
        body{padding:12px; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;}
    }
</style>
</head><body>${content}</body></html>`);
        w.document.close(); w.focus();
        setTimeout(() => w.print(), 400);
    };

    // Inline style helpers
    const sectionBox = { background: bgLight, borderRadius: 8, padding: '12px 14px' };
    const lbl = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: c, marginBottom: 6 };

    return (
        <div dir="ltr" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
            <div style={{ background:'#fff', borderRadius:10, width:'100%', maxWidth:680, maxHeight:'95vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

                {/* Modal header */}
                <div style={{ background:c, color:'#fff', padding:'12px 20px', borderRadius:'10px 10px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontWeight:700, fontSize:17 }}>Invoice {invoiceNo}</span>
                    <div style={{ display:'flex', gap:10 }}>
                        <button onClick={handlePrint} style={{ background:'#fff', color:c, border:'none', padding:'6px 16px', borderRadius:6, fontWeight:700, fontSize:13, cursor:'pointer' }}>🖨️ Print / Save PDF</button>
                        <button onClick={onClose} style={{ background:'transparent', color:'#fff', border:'none', fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
                    </div>
                </div>

                {/* Printable content */}
                <div style={{ overflowY:'auto', flex:1, padding:24 }} ref={printRef}>

                    {/* ── TOP: Logo + INVOICE + Invoice No ── */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', paddingBottom:16, borderBottom:`3px solid ${c}`, marginBottom:20 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                            {logoUrl && <img src={logoUrl} alt="" style={{ height:50, objectFit:'contain', display:'block' }} />}
                            {inv.showCompanyName !== false && <div style={{ fontSize:20, fontWeight:700, color:c }}>{companyName}</div>}
                        </div>
                        <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:30, fontWeight:800, color:c, letterSpacing:-1 }}>INVOICE</div>
                            <div style={{ fontSize:12, color:'#444', marginTop:4 }}>Invoice No: <strong>{invoiceNo}</strong></div>
                        </div>
                    </div>

                    {/* ── Bill To + Order Details ── */}
                    <div style={{ display:'flex', justifyContent:'space-between', gap:12, marginBottom:20, position:'relative' }}>
                        <div style={{ flex:1, ...sectionBox }}>
                            <div style={lbl}>Bill To</div>
                            <div style={{ fontWeight:600, fontSize:14 }}>{customer?.name || order.customer || '—'}</div>
                            {customer?.mobile && <div style={{ fontSize:12, color:'#555', marginTop:3 }}>📞 {customer.mobile}</div>}
                            {showEmail && customer?.email && <div style={{ fontSize:12, color:'#555', marginTop:2 }}>✉ {customer.email}</div>}
                            {addressLine && <div style={{ fontSize:12, color:'#555', marginTop:2 }}>📍 {addressLine}</div>}
                        </div>
                        <div style={{ flex:1, ...sectionBox }}>
                            <div style={lbl}>Order Details</div>
                            {wooOrderId && <div style={{ fontSize:13, marginTop:2 }}><strong>Order ID:</strong> #{wooOrderId}</div>}
                            <div style={{ fontSize:13, marginTop:2 }}><strong>Date:</strong> {orderDate}</div>
                            <div style={{ fontSize:13, marginTop:2 }}><strong>Payment:</strong> {order.payment || 'Cash'}</div>
                        </div>
                        {/* PAID stamp */}
                        {isPaid && (
                            <div style={{
                                position:'absolute', right:16, top:'50%', transform:'translateY(-50%) rotate(-18deg)',
                                border:`3px solid #16a34a`, color:'#16a34a', fontWeight:900, fontSize:28,
                                padding:'4px 18px', borderRadius:8, opacity:0.7, letterSpacing:2,
                                pointerEvents:'none', textTransform:'uppercase'
                            }}>PAID</div>
                        )}
                    </div>

                    {/* ── Items table ── */}
                    <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
                        <thead>
                            <tr style={{ background:c }}>
                                <th style={{ color:'#fff', textAlign:'left', padding:'8px 10px', fontSize:12, width:32 }}>#</th>
                                <th style={{ color:'#fff', textAlign:'left', padding:'8px 4px', fontSize:12, width:44 }}></th>
                                <th style={{ color:'#fff', textAlign:'left', padding:'8px 10px', fontSize:12 }}>Product</th>
                                <th style={{ color:'#fff', textAlign:'center', padding:'8px 10px', fontSize:12, width:52 }}>Qty</th>
                                <th style={{ color:'#fff', textAlign:'right', padding:'8px 10px', fontSize:12, width:100 }}>Unit Price</th>
                                <th style={{ color:'#fff', textAlign:'right', padding:'8px 10px', fontSize:12, width:100 }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, idx) => (
                                <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : bgLight }}>
                                    <td style={{ padding:'8px 10px', fontSize:12, color:'#888', borderBottom:'1px solid #eee' }}>{idx + 1}</td>
                                    <td style={{ padding:'4px', borderBottom:'1px solid #eee' }}>
                                        {item.imageUrl
                                            ? <img src={item.imageUrl} alt="" style={{ width:36, height:36, objectFit:'cover', borderRadius:4, border:'1px solid #ddd', display:'block' }} onError={e => e.target.style.display='none'} />
                                            : null}
                                    </td>
                                    <td style={{ padding:'8px 10px', fontSize:13, fontWeight:500, borderBottom:'1px solid #eee' }}>{item.name}</td>
                                    <td style={{ padding:'8px 10px', fontSize:13, textAlign:'center', borderBottom:'1px solid #eee' }}>{item.qty}</td>
                                    <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right', borderBottom:'1px solid #eee' }}>{currency} {item.unitPrice.toFixed(2)}</td>
                                    <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right', fontWeight:600, borderBottom:'1px solid #eee' }}>{currency} {item.total.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* ── Totals ── */}
                    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:24 }}>
                        <table style={{ borderCollapse:'collapse' }}>
                            <tbody>
                                <tr>
                                    <td style={{ padding:'3px 24px 3px 0', color:'#555', fontSize:13 }}>Subtotal</td>
                                    <td style={{ padding:'3px 0', textAlign:'right', fontWeight:600, fontSize:13 }}>{currency} {subtotal.toFixed(2)}</td>
                                </tr>
                                {shippingFee > 0 && (
                                    <tr>
                                        <td style={{ padding:'3px 24px 3px 0', color:'#555', fontSize:13 }}>Shipping</td>
                                        <td style={{ padding:'3px 0', textAlign:'right', fontSize:13 }}>{currency} {shippingFee.toFixed(2)}</td>
                                    </tr>
                                )}
                                {discount > 0 && (
                                    <tr>
                                        <td style={{ padding:'3px 24px 3px 0', color:'#e00', fontSize:13 }}>Discount</td>
                                        <td style={{ padding:'3px 0', textAlign:'right', color:'#e00', fontSize:13 }}>− {currency} {discount.toFixed(2)}</td>
                                    </tr>
                                )}
                                <tr style={{ borderTop:`2px solid ${c}` }}>
                                    <td style={{ padding:'8px 24px 4px 0', fontWeight:800, fontSize:15 }}>TOTAL</td>
                                    <td style={{ padding:'8px 0 4px', textAlign:'right', fontWeight:800, fontSize:15, color:c }}>{currency} {total.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* ── Customer Notes (before footer) ── */}
                    {customerNotes && (
                        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12 }}>
                            <strong style={{ color:'#92400e' }}>Customer Notes:</strong>
                            <div style={{ color:'#78350f', marginTop:4 }}>{customerNotes}</div>
                        </div>
                    )}

                    {/* ── Footer ── */}
                    <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:12, textAlign:'center', color:'#999', fontSize:11 }}>
                        <div>{footerText}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
